/*
 * PL-AB-030 — "subscribe and save" adds the subscription straight to the cart.
 *
 * Loaded only by one-time-popup-v2.liquid, so the control popup keeps its current
 * behaviour untouched. That matters while the A/B test is running: the control is the
 * other half of the measurement and must not move.
 *
 * WHAT THE CONTROL DOES TODAY
 *
 * The button carries data-subscription-varaint-id, built in Liquid from the first variant
 * whose firmhouse shipment contains "days", and one-time-popup.js looks that id up among
 * the rendered .frequency-box rows. On Plankton Capsules the id it carries
 * (54283223368007) is not any of the rows actually rendered (49812723663175,
 * 49812723630407, 49812723597639), so the lookup fails and selectSubscription() returns
 * early. The button closes the modal and selects nothing.
 *
 * So rather than trusting that attribute, this picks the option out of the picker itself:
 * the first row carrying a selling plan. The picker renders subscriptions longest interval
 * first, which is the "highest interval" Thom asked for, and it stays correct on a product
 * with different intervals because nothing is hardcoded.
 *
 * ADDING TO CART
 *
 * Selecting an option makes the theme refetch the section, which takes about a second and
 * disables the Add to Cart button while it runs. Adding immediately would post the previous
 * variant or hit a disabled button. So this waits for the form to actually show the chosen
 * variant AND the button to come back, then clicks the page's own Add to Cart button so the
 * cart drawer, analytics and the double-submit guard all behave normally.
 *
 * If that state never arrives, it adds nothing. On a money path, doing nothing and leaving
 * the shopper on a correctly-selected page beats adding the wrong thing.
 */
(() => {
  const SETTLE_TIMEOUT_MS = 6000;
  const POLL_MS = 100;

  const onReady = (fn) =>
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn, { once: true })
      : fn();

  onReady(() => {
    document.querySelectorAll('.otp2[data-autoadd]').forEach((modal) => {
      const sectionId = modal.dataset.section;
      const button = modal.querySelector('[data-close-modal]');
      if (!sectionId || !button) return;

      const picker = () =>
        document.querySelector(
          `variant-selects[data-section="${sectionId}"] fieldset.product-form__input--pill, ` +
          `buy-box-value[data-section="${sectionId}"], buy-box-v3[data-section="${sectionId}"]`
        );

      /** The longest-interval subscription: first row that carries a selling plan. */
      const longestSubscriptionInput = () =>
        Array.from(picker()?.querySelectorAll('input[type="radio"]') || []).find(
          (input) => input.dataset.sellingPlanId && !input.disabled
        );

      const form = () => document.getElementById(`product-form-${sectionId}`);
      const addButton = () => document.getElementById(`ProductSubmitButton-${sectionId}`);

      let busy = false;

      button.addEventListener('click', () => {
        // The theme's own handler on this button closes the modal and tries to select.
        // Guard against a second tap while the first is still settling.
        if (busy) return;

        const input = longestSubscriptionInput();
        if (!input) return;

        // The value-framing buy box stores the variant on its radio; the standard
        // picker stores it on the surrounding frequency card.
        const wantedVariant = input.dataset.variantId || input.closest('.variant-wrap')
          ?.querySelector('.frequency-box')?.dataset.variantId?.split('-')[0];
        const wantedPlan = input.dataset.sellingPlanId;
        if (!wantedVariant) return;

        busy = true;

        // Select it the way the picker expects: check, then dispatch from the input, so
        // global.js reads the right selling plan off the event target.
        if (!input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const startedAt = Date.now();
        const tick = () => {
          const f = form();
          const add = addButton();
          const currentVariant = f?.querySelector('input[name="id"]')?.value;
          const currentPlan = f?.querySelector('input[name="selling_plan"]')?.value;
          const ready =
            add &&
            !add.hasAttribute('disabled') &&
            !add.classList.contains('loading') &&
            currentVariant === wantedVariant &&
            currentPlan === wantedPlan;

          if (ready) {
            busy = false;
            add.click();
            return;
          }

          if (Date.now() - startedAt > SETTLE_TIMEOUT_MS) {
            // Give up rather than add something unintended. The shopper is left on the
            // page with the subscription selected, one tap from buying it.
            busy = false;
            return;
          }
          setTimeout(tick, POLL_MS);
        };
        setTimeout(tick, POLL_MS);
      });
    });
  });
})();
